/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import get from 'lodash/get';
import isNull from 'lodash/isNull';

import CommunicatorFirmware from '../../communicator/firmware/communicator-firmware';
import CommunicatorLayer from '../../communicator/layer/communicator-layer';
import CommunicatorLayout from '../../communicator/layout/communicator-layout';
import ErrorLevel from '../../error/error-level';

// TODO #94 Přidat unit testy pro nové funkcionality spojené s offline módem.
/**
 * Komunikátor obsahu zařízení pro Ki-Wi Server
 */
class ContentCommunicator {
  /**
   * Vytvoří instanci.
   * @param {CommunicatorSession} communicatorSession
   * @param {CommunicatorSettingsFactory} communicatorSettingsFactory
   * @param {Object} config Konfigurace aplikace.
   * @param {ContentTaskCommunicator} contentTaskCommunicator
   * @param {FirmwareManager} firmwareManager
   * @param {MediumCommunicator} mediumCommunicator
   * @param {PlaylistCommunicator} playlistCommunicator
   * @param {PowerTaskCommunicator} powerTaskCommunicator
   * @param {TaskCommunicator} taskCommunicator
   */
  constructor(
    communicatorSession,
    communicatorSettingsFactory,
    config,
    contentTaskCommunicator,
    firmwareManager,
    mediumCommunicator,
    playlistCommunicator,
    powerTaskCommunicator,
    taskCommunicator
  ) {
    const communicatorLayer = new CommunicatorLayer();
    this.communicatorFirmware = new CommunicatorFirmware(config);
    this.communicatorLayer = communicatorLayer;
    this.communicatorLayout = new CommunicatorLayout(communicatorLayer);
    this.communicatorSession = communicatorSession;
    this.communicatorSettingsFactory = communicatorSettingsFactory;
    this.contentTaskCommunicator = contentTaskCommunicator;
    this.fetching = false;
    this.firmwareManager = firmwareManager;
    this.latestTimestamp = null;
    this.mediumCommunicator = mediumCommunicator;
    this.playlistCommunicator = playlistCommunicator;
    this.powerTaskCommunicator = powerTaskCommunicator;
    this.taskCommunicator = taskCommunicator;
  }

  /**
   * Porovná, jestli obdržený časový otisk uspokojí požadovaný časový otisk.
   * @param {string} [receivedTimestamp] Obdržený časový otisk z obsahového API.
   * @param {string} [latestTimestamp] Požadovaný časový otisk z heartbeat API.
   * @private
   * @returns {boolean} Příznak, jestli časový otisk uspokojí požadovaný.
   */
  static compareReceived(receivedTimestamp, latestTimestamp) {
    const receivedDate = new Date(receivedTimestamp);
    const receivedValid = !isNull(receivedTimestamp) && !isNaN(receivedDate);
    const latestDate = new Date(latestTimestamp);
    const latestValid = !isNull(latestTimestamp) && !isNaN(latestDate);
    return receivedValid && (!latestValid || !(receivedDate < latestDate));
  }

  /**
   * Porovná, jestli má požadovaný časový otisk nahradit předchozí časový otisk.
   * @param {string} [requiredTimestamp] Nově požadovaný časový otisk z heartbeat API.
   * @param {string} [latestTimestamp] Předchozí požadovaný časový otisk z heartbeat API.
   * @private
   * @returns {boolean} Příznak, jestli má nový časový otisk nahradit předchozí.
   */
  static compareRequired(requiredTimestamp, latestTimestamp) {
    const requiredDate = new Date(requiredTimestamp);
    const requiredValid = !isNull(requiredTimestamp) && !isNaN(requiredDate);
    const latestDate = new Date(latestTimestamp);
    const latestValid = !isNull(latestTimestamp) && !isNaN(latestDate);
    return requiredValid && (!latestValid || requiredDate > latestDate);
  }

  /**
   * Doručí obsah zařízení.
   * @param {Store} store Datový sklad ke vložení obsahu zařízení.
   * @param {string} [requiredTimestamp] Časový otisk, který by měl obsah mít.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení, které chce komunikovat.
   * @param {string} serverUrl URL adresa Ki-Wi Server pro komunikaci.
   * @returns {Promise.<ContentInfo|null>} Informace o novém obsahu zařízení.
   */
  fetch(store, requiredTimestamp, deviceId, serverUrl) {
    // TODO #94 Omezení paralelního fetch:
    // - Napsat unit testy.
    // TODO #84 Reakce při chybě nebo neaktuálním obsahu.
    // - Ponechány debug výpisy pro usnadění testování.
    // - Zatím je řízeno z heartbeat. Propracovanější verze by měla řešit problém zde.
    // - Obecně jde o to, že pokud se stahuje obsah 1 a mezitím přijde požadavek na obsah 2.
    // - V tom případě by se mělo počkat na stažení 1, zahodit a potom (ihned?) stáhnout obsah 2.
    // - Nyní se spoléhá na to, že přijde další heartbeat, který nový obsah stáhne.
    // Uložení nejnovějšího požadovaného contentTimestamp.
    if (
      ContentCommunicator.compareRequired(
        requiredTimestamp,
        this.latestTimestamp
      )
    ) {
      this.latestTimestamp = requiredTimestamp;
    }
    if (this.fetching) {
      // Pokud se již nějaký obsah stahuje, tak se již nic jiného nedělat.
      return Promise.resolve(null);
    }
    this.fetching = true;
    return (
      // Stažení JSON obsahu zařízení.
      fetch(`${serverUrl}/device/generator/${deviceId}/3`)
        // Snížení priority logování pro chyby síťového spojení.
        .catch((error) => {
          error.level = ErrorLevel.Warning;
          throw error;
        })
        .then((response) => {
          // Snížení priority logování pro chyby ze serveru.
          if (!response.ok) {
            const error = new RangeError(
              `Invalid HTTP status "${response.status}".`
            );
            error.level = ErrorLevel.Warning;
            throw error;
          }
          return response.json();
        })
        .then((json) =>
          this.firmwareManager.getInfo().then((firmwareInfo) => {
            const contentInfo = this.parse(store, json, firmwareInfo);
            const receivedTimestamp = contentInfo.session.contentTimestamp;
            if (
              ContentCommunicator.compareReceived(
                receivedTimestamp,
                this.latestTimestamp
              )
            ) {
              return contentInfo;
            }
            return null;
          })
        )
        .finally(() => {
          this.fetching = false;
        })
    );
  }

  /**
   * Zpracuje obsah zařízení.
   * @param {Store} store Datový sklad ke vložení obsahu zařízení.
   * @param {Object} [json] Odpověď na obsah zařízení.
   * @param {FirmwareInfo} firmwareInfo Informace o aktuálním firmware zařízení.
   * @private
   * @returns {ContentInfo} Informace o novém obsahu zařízení.
   */
  parse(store, json, firmwareInfo) {
    // Načtení nastavení zařízení.
    const communicatorSettings = this.communicatorSettingsFactory.create(
      get(json, 'main.module.media[0].settings')
    );
    // Načtení jednotlivých částí obsahu.
    const contentTasksJson = get(json, 'main.commonContent.task') ?? [];
    const contentTasks = contentTasksJson.map((contentTaskJson) =>
      this.contentTaskCommunicator.parse(contentTaskJson, store)
    );
    const powerTasksJson = get(json, 'main.module.task') ?? [];
    const { powerTasks, timerArray } = powerTasksJson.reduce(
      (acc, powerTaskJson) => {
        // #472 - Hotfix: Ignorování jiných typů control tasků, než 'shutdown'
        // TODO: doplnit unit testy, přejmenovat parse na parseShutdown
        if (powerTaskJson.type === 'shutdown') {
          const group = this.powerTaskCommunicator.parse(powerTaskJson, store);
          acc.powerTasks.push(group.powerTask);
          acc.timerArray.push(...group.timerArray);
        }
        return acc;
      },
      { powerTasks: [], timerArray: [] }
    );
    const taskArray = this.taskCommunicator.parse(
      contentTasks,
      powerTasks,
      store
    );
    const mediumArrayJson = get(json, 'main.commonContent.medium') ?? [];
    const mediumArray = mediumArrayJson.map((mediumJson) =>
      this.mediumCommunicator.parse(mediumJson, store)
    );
    const playlistDsMap = this.playlistCommunicator.parseMany(
      get(json, 'main.commonContent.playlist')
    );
    const layoutArrayJson = get(json, 'main.commonContent.layout') ?? [];
    const { layerArray, layoutArray } = layoutArrayJson.reduce(
      (acc, layoutJson) => {
        const layout = this.communicatorLayout.parse(store, layoutJson);
        // Odebírá se výchozí vkládáný layout.
        // Není k ničemu používaný a ani se nezdá k něčemu vhodný.
        if (layout.id !== 'mainLayout') {
          acc.layoutArray.push(layout);
          acc.layerArray.push(
            ...this.communicatorLayout.parseLayers(store, layoutJson)
          );
        }
        return acc;
      },
      { layerArray: [], layoutArray: [] }
    );
    // Složení výsledného obsahu z jednotlivých částí.
    return {
      contentTasks,
      firmwareRelease: this.communicatorFirmware.parse(
        communicatorSettings,
        firmwareInfo
      ),
      layerArray,
      layoutArray,
      mediumArray,
      playlistArray: store.inject(
        'playlist',
        Object.values(playlistDsMap.playlist)
      ),
      powerTasks,
      session: this.communicatorSession.parse(
        communicatorSettings,
        json,
        store
      ),
      store,
      taskArray,
      timerArray,
      trackArray: store.inject('track', Object.values(playlistDsMap.track))
    };
  }
}

export default ContentCommunicator;
